import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Text,
  Button,
  Input,
  Avatar,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  makeStyles,
  shorthands,
  tokens,
  Badge,
} from '@fluentui/react-components';
import {
  Person24Regular,
  Location24Regular,
  Payment24Regular,
  History24Regular,
  Shield24Regular,
  Edit24Regular,
  Add24Regular,
  Delete24Regular,
  Star24Filled,
  ChevronRight24Regular,
  CreditCardPerson24Regular,
  LockClosed24Regular,
  Key24Regular,
  Mail24Regular,
  Call24Regular,
} from '@fluentui/react-icons';
import { usePortalAuth } from '../contexts/PortalAuthContext';
import { CustomerAddress, ActivityLogEntry } from '../types';
import { formatBusinessTime, formatBusinessDate } from '../../lib/businessTime';

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: '24px',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  sidebar: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    ...shorthands.overflow('hidden'),
    height: 'fit-content',
  },
  sidebarHeader: {
    ...shorthands.padding('24px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  sidebarName: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  sidebarEmail: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  navList: {
    ...shorthands.padding('8px'),
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderRadius('8px'),
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  navLabel: {
    flex: 1,
    fontSize: '14px',
  },
  mainContent: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    ...shorthands.padding('24px'),
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '24px',
  },
  formSection: {
    marginBottom: '32px',
  },
  formSectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  formLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground2,
  },
  addressCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    ...shorthands.padding('16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('8px'),
    marginBottom: '12px',
  },
  addressCardPrimary: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  addressContent: {
    flex: 1,
  },
  addressLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  addressLabelText: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  addressText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.5',
  },
  addressActions: {
    display: 'flex',
    gap: '4px',
  },
  paymentCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    ...shorthands.padding('16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('8px'),
    marginBottom: '12px',
  },
  paymentInfo: {
    flex: 1,
  },
  paymentLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  paymentDetails: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  securityItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('8px'),
    marginBottom: '12px',
  },
  securityInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  securityLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  securityDescription: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    textAlign: 'center' as const,
    ...shorthands.padding('32px'),
    color: tokens.colorNeutralForeground3,
  },
  // Activity log styles
  activityCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    ...shorthands.overflow('hidden'),
  },
  dateGroup: {
    ...shorthands.padding('12px', '16px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  dateGroupTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
  },
  activityItem: {
    display: 'flex',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  activityIcon: {
    width: '36px',
    height: '36px',
    ...shorthands.borderRadius('8px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  activityIconProfile: {
    backgroundColor: tokens.colorPaletteBlueBorderActive,
    color: '#ffffff',
  },
  activityIconAddress: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: '#ffffff',
  },
  activityIconPayment: {
    backgroundColor: tokens.colorPalettePurpleBackground2,
    color: '#ffffff',
  },
  activityIconEmail: {
    backgroundColor: tokens.colorPaletteYellowBackground3,
    color: tokens.colorNeutralForeground1,
  },
  activityIconPhone: {
    backgroundColor: tokens.colorPaletteTealBackground2,
    color: '#ffffff',
  },
  activityIconPassword: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: '#ffffff',
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '2px',
  },
  activityDescription: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  activityMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '4px',
  },
  activityTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
});

interface NavItem {
  id: string;
  path: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { id: 'profile', path: '/portal/account', label: 'Profile', icon: <Person24Regular /> },
  { id: 'addresses', path: '/portal/account/addresses', label: 'Addresses', icon: <Location24Regular /> },
  { id: 'payment', path: '/portal/account/payment', label: 'Payment Methods', icon: <Payment24Regular /> },
  { id: 'activity', path: '/portal/account/activity', label: 'Activity Log', icon: <History24Regular /> },
  { id: 'security', path: '/portal/account/security', label: 'Security', icon: <Shield24Regular /> },
];

// Mock addresses
const mockAddresses: CustomerAddress[] = [
  {
    id: 'addr-1',
    label: 'Main Office',
    address1: '123 Main Street',
    city: 'San Juan',
    region: 'PR',
    zipcode: '00901',
    country: 'US',
    isPrimary: true,
  },
  {
    id: 'addr-2',
    label: 'Construction Site',
    address1: '456 Oak Avenue',
    city: 'Bayamón',
    region: 'PR',
    zipcode: '00959',
    country: 'US',
    isPrimary: false,
  },
];

// Mock payment methods
const mockPaymentMethods = [
  {
    id: 'pm-1',
    type: 'card',
    brand: 'Visa',
    last4: '4242',
    expiryMonth: 12,
    expiryYear: 2026,
    isDefault: true,
  },
];

// Mock activity data
const mockActivityLog: ActivityLogEntry[] = [
  {
    id: 'act-1',
    timestamp: new Date().toISOString(),
    action: 'update',
    entityType: 'address',
    description: 'Address Updated',
    changes: [
      { field: 'address1', oldValue: '123 Main St', newValue: '123 Main Street' },
    ],
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-2',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    action: 'create',
    entityType: 'payment_method',
    description: 'Payment Method Added',
    changes: [
      { field: 'card', newValue: 'Visa ending in 4242' },
    ],
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-3',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    action: 'update',
    entityType: 'email',
    description: 'Email Updated',
    changes: [
      { field: 'email', oldValue: 'old@email.com', newValue: 'carmen@example.com' },
    ],
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-4',
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    action: 'update',
    entityType: 'phone',
    description: 'Phone Updated',
    changes: [
      { field: 'phone', oldValue: '(787) 555-0000', newValue: '(787) 555-1234' },
    ],
    actor: { type: 'admin', name: 'TWM Admin', id: 'admin-1' },
  },
  {
    id: 'act-5',
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    action: 'update',
    entityType: 'password',
    description: 'Password Changed',
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-6',
    timestamp: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    action: 'create',
    entityType: 'address',
    description: 'Address Added',
    changes: [
      { field: 'address', newValue: '456 Oak Avenue, Bayamón, PR 00959' },
    ],
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-7',
    timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    action: 'create',
    entityType: 'profile',
    description: 'Account Created',
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
];

const Account: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = usePortalAuth();
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    setAddresses(mockAddresses);
  }, []);

  // Determine which section to show based on path
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path === '/portal/account/addresses') return 'addresses';
    if (path === '/portal/account/payment') return 'payment';
    if (path === '/portal/account/security') return 'security';
    if (path === '/portal/account/activity') return 'activity';
    return 'profile';
  };

  const currentSection = getCurrentSection();
  const isActive = (path: string) => location.pathname === path;

  const handleSaveProfile = () => {
    setEditDialogOpen(false);
  };

  const handleChangePassword = () => {
    setPasswordDialogOpen(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  // Render Profile Section
  const renderProfileSection = () => (
    <>
      <Text className={styles.sectionTitle}>Profile Information</Text>

      <div className={styles.formSection}>
        <div className={styles.formSectionTitle}>
          <span>Personal Information</span>
          <Dialog open={editDialogOpen} onOpenChange={(e, data) => setEditDialogOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="subtle" icon={<Edit24Regular />}>
                Edit
              </Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Edit Profile</DialogTitle>
                <DialogContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                    <div className={styles.formField}>
                      <label className={styles.formLabel} htmlFor="profile-name">Name</label>
                      <Input
                        id="profile-name"
                        value={formData.name}
                        onChange={(e, data) => setFormData({ ...formData, name: data.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel} htmlFor="profile-email">Email</label>
                      <Input
                        id="profile-email"
                        type="email"
                        value={formData.email}
                        onChange={(e, data) => setFormData({ ...formData, email: data.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel} htmlFor="profile-phone">Phone</label>
                      <Input
                        id="profile-phone"
                        value={formData.phone}
                        onChange={(e, data) => setFormData({ ...formData, phone: data.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel} htmlFor="profile-company">Company</label>
                      <Input
                        id="profile-company"
                        value={formData.company}
                        onChange={(e, data) => setFormData({ ...formData, company: data.value })}
                      />
                    </div>
                  </div>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Cancel</Button>
                  </DialogTrigger>
                  <Button appearance="primary" onClick={handleSaveProfile}>
                    Save Changes
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
        <div className={styles.formGrid}>
          <div className={styles.formField}>
            <Text className={styles.formLabel}>Name</Text>
            <Text>{user?.name || 'Not set'}</Text>
          </div>
          <div className={styles.formField}>
            <Text className={styles.formLabel}>Email</Text>
            <Text>{user?.email || 'Not set'}</Text>
          </div>
          <div className={styles.formField}>
            <Text className={styles.formLabel}>Phone</Text>
            <Text>{user?.phone || '(787) 555-1234'}</Text>
          </div>
          <div className={styles.formField}>
            <Text className={styles.formLabel}>Company</Text>
            <Text>{user?.company || 'Rivera Construction LLC'}</Text>
          </div>
        </div>
      </div>
    </>
  );

  // Render Addresses Section
  const renderAddressesSection = () => (
    <>
      <Text className={styles.sectionTitle}>Service Addresses</Text>

      <div className={styles.formSection}>
        <div className={styles.formSectionTitle}>
          <span>Your Addresses</span>
          <Button appearance="subtle" icon={<Add24Regular />}>
            Add Address
          </Button>
        </div>
        {addresses.length === 0 ? (
          <div className={styles.emptyState}>
            <Location24Regular style={{ fontSize: '48px', marginBottom: '16px' }} />
            <Text>No addresses added yet</Text>
          </div>
        ) : (
          addresses.map((address) => (
            <div
              key={address.id}
              className={`${styles.addressCard} ${address.isPrimary ? styles.addressCardPrimary : ''}`}
            >
              <Location24Regular />
              <div className={styles.addressContent}>
                <div className={styles.addressLabel}>
                  <Text className={styles.addressLabelText}>{address.label || 'Address'}</Text>
                  {address.isPrimary && (
                    <Star24Filled style={{ color: tokens.colorPaletteYellowForeground1, fontSize: '16px' }} />
                  )}
                </div>
                <Text className={styles.addressText}>
                  {address.address1}<br />
                  {address.city}, {address.region} {address.zipcode}
                </Text>
              </div>
              <div className={styles.addressActions}>
                <Button appearance="subtle" size="small" icon={<Edit24Regular />} />
                {!address.isPrimary && (
                  <Button appearance="subtle" size="small" icon={<Delete24Regular />} />
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  // Render Payment Methods Section
  const renderPaymentSection = () => (
    <>
      <Text className={styles.sectionTitle}>Payment Methods</Text>

      <div className={styles.formSection}>
        <div className={styles.formSectionTitle}>
          <span>Saved Cards</span>
          <Button appearance="subtle" icon={<Add24Regular />}>
            Add Card
          </Button>
        </div>
        {mockPaymentMethods.length === 0 ? (
          <div className={styles.emptyState}>
            <CreditCardPerson24Regular style={{ fontSize: '48px', marginBottom: '16px' }} />
            <Text>No payment methods added yet</Text>
          </div>
        ) : (
          mockPaymentMethods.map((pm) => (
            <div key={pm.id} className={styles.paymentCard}>
              <Payment24Regular />
              <div className={styles.paymentInfo}>
                <Text className={styles.paymentLabel}>
                  {pm.brand} •••• {pm.last4}
                  {pm.isDefault && (
                    <Badge appearance="filled" color="brand" style={{ marginLeft: '8px' }}>
                      Default
                    </Badge>
                  )}
                </Text>
                <Text className={styles.paymentDetails}>
                  Expires {pm.expiryMonth}/{pm.expiryYear}
                </Text>
              </div>
              <div className={styles.addressActions}>
                <Button appearance="subtle" size="small" icon={<Edit24Regular />} />
                <Button appearance="subtle" size="small" icon={<Delete24Regular />} />
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  // Render Security Section
  const renderSecuritySection = () => (
    <>
      <Text className={styles.sectionTitle}>Security Settings</Text>

      <div className={styles.formSection}>
        <div className={styles.securityItem}>
          <div className={styles.securityInfo}>
            <LockClosed24Regular />
            <div>
              <Text className={styles.securityLabel}>Password</Text>
              <Text className={styles.securityDescription}>Last changed 30 days ago</Text>
            </div>
          </div>
          <Dialog open={passwordDialogOpen} onOpenChange={(e, data) => setPasswordDialogOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="subtle">Change Password</Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                    <div className={styles.formField}>
                      <label className={styles.formLabel} htmlFor="current-password">Current Password</label>
                      <Input
                        id="current-password"
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e, data) => setPasswordData({ ...passwordData, currentPassword: data.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel} htmlFor="new-password">New Password</label>
                      <Input
                        id="new-password"
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e, data) => setPasswordData({ ...passwordData, newPassword: data.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label className={styles.formLabel} htmlFor="confirm-password">Confirm New Password</label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e, data) => setPasswordData({ ...passwordData, confirmPassword: data.value })}
                      />
                    </div>
                  </div>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Cancel</Button>
                  </DialogTrigger>
                  <Button appearance="primary" onClick={handleChangePassword}>
                    Update Password
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>

        <div className={styles.securityItem}>
          <div className={styles.securityInfo}>
            <Key24Regular />
            <div>
              <Text className={styles.securityLabel}>Two-Factor Authentication</Text>
              <Text className={styles.securityDescription}>Add an extra layer of security</Text>
            </div>
          </div>
          <Badge appearance="outline" color="warning">Not Enabled</Badge>
        </div>

        <div className={styles.securityItem}>
          <div className={styles.securityInfo}>
            <History24Regular />
            <div>
              <Text className={styles.securityLabel}>Login History</Text>
              <Text className={styles.securityDescription}>View recent account activity</Text>
            </div>
          </div>
          <Button appearance="subtle" onClick={() => navigate('/portal/account/activity')}>
            View Activity
          </Button>
        </div>
      </div>
    </>
  );

  // Activity log helpers
  const getActivityIcon = (entityType: string) => {
    const iconClass = styles.activityIcon;
    switch (entityType) {
      case 'profile':
        return <div className={`${iconClass} ${styles.activityIconProfile}`}><Person24Regular /></div>;
      case 'address':
        return <div className={`${iconClass} ${styles.activityIconAddress}`}><Location24Regular /></div>;
      case 'payment_method':
        return <div className={`${iconClass} ${styles.activityIconPayment}`}><Payment24Regular /></div>;
      case 'email':
        return <div className={`${iconClass} ${styles.activityIconEmail}`}><Mail24Regular /></div>;
      case 'phone':
        return <div className={`${iconClass} ${styles.activityIconPhone}`}><Call24Regular /></div>;
      case 'password':
        return <div className={`${iconClass} ${styles.activityIconPassword}`}><Shield24Regular /></div>;
      default:
        return <div className={iconClass}><Person24Regular /></div>;
    }
  };

  const formatTime = (dateString: string) => formatBusinessTime(dateString);

  const getDateGroup = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return formatBusinessDate(dateString);
  };

  // Group activities by date
  const groupedActivities = mockActivityLog.reduce((groups, activity) => {
    const dateGroup = getDateGroup(activity.timestamp);
    if (!groups[dateGroup]) {
      groups[dateGroup] = [];
    }
    groups[dateGroup].push(activity);
    return groups;
  }, {} as Record<string, ActivityLogEntry[]>);

  // Render Activity Log Section
  const renderActivitySection = () => (
    <>
      <Text className={styles.sectionTitle}>Activity Log</Text>

      <div className={styles.activityCard}>
        {Object.entries(groupedActivities).map(([dateGroup, groupActivities]) => (
          <div key={dateGroup}>
            <div className={styles.dateGroup}>
              <Text className={styles.dateGroupTitle}>{dateGroup}</Text>
            </div>
            {groupActivities.map((activity) => (
              <div key={activity.id} className={styles.activityItem}>
                {getActivityIcon(activity.entityType)}
                <div className={styles.activityContent}>
                  <Text className={styles.activityTitle}>{activity.description}</Text>
                  {activity.changes?.map((change, index) => (
                    <div key={index}>
                      {change.oldValue && change.newValue ? (
                        <Text className={styles.activityDescription}>
                          {change.oldValue} → {change.newValue}
                        </Text>
                      ) : change.newValue ? (
                        <Text className={styles.activityDescription}>
                          {change.newValue}
                        </Text>
                      ) : null}
                    </div>
                  ))}
                  <div className={styles.activityMeta}>
                    <Text className={styles.activityTime}>{formatTime(activity.timestamp)}</Text>
                    <Text className={styles.activityTime}>By: {activity.actor.name}</Text>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );

  // Render the appropriate section content
  const renderContent = () => {
    switch (currentSection) {
      case 'addresses':
        return renderAddressesSection();
      case 'payment':
        return renderPaymentSection();
      case 'security':
        return renderSecuritySection();
      case 'activity':
        return renderActivitySection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <div className={styles.container}>
      {/* Sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Avatar name={user?.name} size={72} color="colorful" />
          <Text className={styles.sidebarName}>{user?.name}</Text>
          <Text className={styles.sidebarEmail}>{user?.email}</Text>
        </div>
        <div className={styles.navList}>
          {navItems.map((item) => (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              aria-current={isActive(item.path) ? 'page' : undefined}
              aria-label={item.label}
              className={`${styles.navItem} ${isActive(item.path) ? styles.navItemActive : ''}`}
              onClick={() => navigate(item.path)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(item.path);
                }
              }}
            >
              {item.icon}
              <Text className={styles.navLabel}>{item.label}</Text>
              <ChevronRight24Regular />
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {renderContent()}
      </div>
    </div>
  );
};

export default Account;
